import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, message, Switch, Popconfirm, Flex, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const Products = () => {
    const [products, setProducts] = useState([
        {
            id: 1,
            name: 'Futbol Topu',
            sku: 'FB-001',
            purchasePrice: 250,
            sellingPrice: 350,
            category: 'Futbol Ekipmanları',
            brand: 'Nike',
            isSaleable: true
        },
        {
            id: 2,
            name: 'Basketbol Topu',
            sku: 'BB-001',
            purchasePrice: 300,
            sellingPrice: 450,
            category: 'Basketbol Ekipmanları',
            brand: 'Spalding',
            isSaleable: false
        },
    ]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const [isSaleableValue, setIsSaleableValue] = useState(true);

    useEffect(() => {
        if (isModalVisible) {
            const currentValue = form.getFieldValue('isSaleable');
            setIsSaleableValue(currentValue ?? true);
        }
    }, [isModalVisible, form]);

    const handleSaleableChange = (checked) => {
        setIsSaleableValue(checked);
        if (!checked) {
            form.setFieldValue('sellingPrice', null);
        }
    };

    const columns = [
        { 
            title: 'Ürün Adı', 
            dataIndex: 'name', 
            key: 'name',
            fixed: 'left',
            width: {
                xs: 280,
                sm: 250,
                md: 200,
                lg: 200
            },
            ellipsis: true
        },
        { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 120 },
        { title: 'Kategori', dataIndex: 'category', key: 'category', width: 150 },
        { title: 'Marka', dataIndex: 'brand', key: 'brand', width: 120 },
        {
            title: 'Alış Fiyatı',
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            width: 120,
            render: (price) => `₺${price}`
        },
        {
            title: 'Satış Fiyatı',
            dataIndex: 'sellingPrice',
            key: 'sellingPrice',
            width: 120,
            render: (price, record) => {
                if (!record.isSaleable) {
                    return <span style={{ color: '#999' }}>Satılamaz</span>;
                }
                return price ? `₺${price}` : '-';
            }
        },
        {
            title: 'Satılabilir',
            dataIndex: 'isSaleable',
            key: 'isSaleable',
            width: 100,
            render: (isSaleable) => (
                <span style={{ color: isSaleable ? '#52c41a' : '#ff4d4f' }}>
                    {isSaleable ? 'Evet' : 'Hayır'}
                </span>
            )
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        type="primary"
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Ürünü silmek istediğinizden emin misiniz?"
                        okText="Evet"
                        cancelText="Hayır"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            type="primary"
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        setProducts(products.filter(product => product.id !== id));
        message.success('Ürün silindi');
    };

    const handleModalOk = () => {
        form.validateFields().then(values => {
            if (editingId) {
                setProducts(products.map(product =>
                    product.id === editingId ? { ...values, id: editingId } : product
                ));
                message.success('Ürün güncellendi');
            } else {
                setProducts([...products, { ...values, id: products.length + 1 }]);
                message.success('Ürün eklendi');
            }
            setIsModalVisible(false);
            form.resetFields();
            setEditingId(null);
        }).catch(error => {
            console.error('Form validation failed:', error);
        });
    };

    return (
        <Flex vertical gap="middle" style={{ padding: 24 }}>
            <Row>
                <Col xs={24} sm={24} md={24} lg={24}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingId(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}
                    >
                        Yeni Ürün Ekle
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col xs={24} sm={24} md={24} lg={24}>
                    <Table 
                        columns={columns} 
                        dataSource={products} 
                        rowKey="id"
                        scroll={{ x: { xs: 1000, sm: 1200, md: 1200, lg: 1200 } }}
                        responsive={['sm', 'md', 'lg']}
                    />
                </Col>
            </Row>

            <Modal
                title={editingId ? "Ürün Düzenle" : "Yeni Ürün Ekle"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingId(null);
                }}
                width={720}
                centered
            >
                <Form form={form} layout="vertical">
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="name"
                                label="Ürün Adı"
                                rules={[{ required: true, message: 'Lütfen ürün adı girin!' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="sku"
                                label="SKU"
                                rules={[{ required: true, message: 'Lütfen SKU girin!' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="category"
                                label="Kategori"
                                rules={[{ required: true, message: 'Lütfen kategori girin!' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="brand"
                                label="Marka"
                                rules={[{ required: true, message: 'Lütfen marka girin!' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="purchasePrice"
                                label="Alış Fiyatı"
                                rules={[{ required: true, message: 'Lütfen alış fiyatı girin!' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    prefix="₺"
                                    min={0}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="sellingPrice"
                                label="Satış Fiyatı"
                                rules={[
                                    {
                                        required: isSaleableValue,
                                        message: 'Lütfen satış fiyatı girin!'
                                    }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    prefix="₺"
                                    min={0}
                                    disabled={!isSaleableValue}
                                    placeholder={isSaleableValue ? "Satış fiyatı girin" : "Satılamaz ürün"}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="isSaleable"
                                label="Satılabilir"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch
                                    checkedChildren="Evet"
                                    unCheckedChildren="Hayır"
                                    onChange={handleSaleableChange}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Flex>
    );
};

export default Products; 